export interface BotColorDef {
  id: string;
  name: string;
  light: string;
  dark: string;
  eyeColor: string;
  hex: string;
}

/** Boo Crew identities from docs/product/Branding/brandkit.md. */
export const BOT_COLOR_LIST: BotColorDef[] = [
  {
    id: "pearl",
    name: "Pearl",
    light: "#FFFFFF",
    dark: "#F8F6EC",
    eyeColor: "#0A1024",
    hex: "#FFFFFF",
  },
  {
    id: "iris",
    name: "Iris",
    light: "#9872FF",
    dark: "#7751D3",
    eyeColor: "#FFFFFF",
    hex: "#875DEF",
  },
  {
    id: "mint",
    name: "Mint",
    light: "#78CCA0",
    dark: "#34885F",
    eyeColor: "#0A1024",
    hex: "#62B785",
  },
  {
    id: "tangerine",
    name: "Tangerine",
    light: "#FF914F",
    dark: "#BC652B",
    eyeColor: "#0A1024",
    hex: "#E07932",
  },
  {
    id: "lagoon",
    name: "Lagoon",
    light: "#83CBE4",
    dark: "#2F88A8",
    eyeColor: "#0A1024",
    hex: "#64B4D1",
  },
  {
    id: "cobalt",
    name: "Cobalt",
    light: "#7998FF",
    dark: "#4165CF",
    eyeColor: "#FFFFFF",
    hex: "#5D80EF",
  },
  {
    id: "honey",
    name: "Honey",
    light: "#F0CB57",
    dark: "#A77F16",
    eyeColor: "#0A1024",
    hex: "#DCB53B",
  },
  {
    id: "taupe",
    name: "Taupe",
    light: "#B49989",
    dark: "#705749",
    eyeColor: "#FFFFFF",
    hex: "#866F64",
  },
  {
    id: "coral",
    name: "Coral",
    light: "#F36F68",
    dark: "#B83F3B",
    eyeColor: "#FFFFFF",
    hex: "#D44F4A",
  },
  {
    id: "berry",
    name: "Berry",
    light: "#F16CB1",
    dark: "#B33578",
    eyeColor: "#FFFFFF",
    hex: "#D25297",
  },
  {
    id: "slate",
    name: "Slate",
    light: "#A0ACC4",
    dark: "#526078",
    eyeColor: "#FFFFFF",
    hex: "#697489",
  },
];

export const BOT_AVATAR_COLORS = BOT_COLOR_LIST.map((c) => c.hex);

/** Shared identity color for Avatar Studio reset/fallback (Iris). */
export const DEFAULT_BOT_AVATAR_COLOR = BOT_COLOR_LIST.find((color) => color.id === "iris")!.hex;

export function shippedHash(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
  }
  return hash >>> 0;
}

export function shippedRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value + 1831565813) | 0;
    let next = Math.imul(value ^ (value >>> 15), 1 | value);
    next = (next + Math.imul(next ^ (next >>> 7), 61 | next)) ^ next;
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

export function resolvePersonaColorDef(
  identity: string,
  explicitColor?: string | null,
): BotColorDef {
  if (explicitColor) {
    const clean = explicitColor.toLowerCase();
    const foundById = BOT_COLOR_LIST.find((c) => c.id === clean || c.name.toLowerCase() === clean);
    if (foundById) return foundById;
    const foundByHex = BOT_COLOR_LIST.find((c) => c.hex.toLowerCase() === clean);
    if (foundByHex) return foundByHex;
    if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(explicitColor)) {
      const isBright = isColorBright(explicitColor);
      return {
        id: "custom",
        name: "Custom",
        light: explicitColor,
        dark: darkenHex(explicitColor, 20),
        eyeColor: isBright ? "#141414" : "#FFFFFF",
        hex: explicitColor,
      };
    }
  }
  const seed = (shippedHash(identity) ^ Math.imul(1, 2654435769)) >>> 0;
  const index = Math.floor(shippedRandom((seed ^ 2654435769) >>> 0)() * BOT_COLOR_LIST.length);
  return BOT_COLOR_LIST[index % BOT_COLOR_LIST.length] ?? BOT_COLOR_LIST[0]!;
}

/** WCAG contrast ratio between two hex colors. */
export function contrastRatio(a: string, b: string): number {
  const [high, low] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return ((high ?? 0) + 0.05) / ((low ?? 0) + 0.05);
}

function relativeLuminance(hex: string): number {
  const c = expandHex(hex);
  const [r, g, b] = [0, 2, 4].map((offset) => {
    const channel = (Number.parseInt(c.substring(offset, offset + 2), 16) || 0) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

function expandHex(hex: string): string {
  const c = hex.replace("#", "");
  if (c.length === 3) {
    const r = c[0] ?? "0";
    const g = c[1] ?? "0";
    const b = c[2] ?? "0";
    return `${r}${r}${g}${g}${b}${b}`;
  }
  return c;
}

function isColorBright(hex: string): boolean {
  const c = expandHex(hex);
  const r = Number.parseInt(c.substring(0, 2), 16) || 0;
  const g = Number.parseInt(c.substring(2, 4), 16) || 0;
  const b = Number.parseInt(c.substring(4, 6), 16) || 0;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 180;
}

function darkenHex(hex: string, percent: number): string {
  const num = Number.parseInt(expandHex(hex), 16);
  const factor = 1 - percent / 100;
  const r = Math.max(0, Math.floor(((num >> 16) & 255) * factor));
  const g = Math.max(0, Math.floor(((num >> 8) & 255) * factor));
  const b = Math.max(0, Math.floor((num & 255) * factor));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
